import React from "react";
import {
  Avatar,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import {
  ActionModal,
  NoticeboardButton,
  ACTION_MODAL_FOOTER_PRIMARY_SIZE,
} from "../../shared";
import type { ChildSchoolFees, ParentChild } from "../../../types/parentFamily";
import {
  getChildFullName,
  getChildInitials,
} from "../../../utils/parentFamilyHelpers";
import { getAvatarColor } from "../../../utils/avatarUtils";
import { lightTheme } from "../../../styles/theme";
import {
  buildFeeLedger,
  dayTypeLabel,
  formatLedgerCurrency,
  formatLedgerDate,
  paymentPlanLabel,
  summarizeSchoolFees,
} from "../../../utils/schoolFeesHelpers";
import { downloadFeeStatementPdf } from "../../../utils/feeStatementPdfExport";

interface FeeStatementViewModalProps {
  open: boolean;
  onClose: () => void;
  account: ChildSchoolFees | null;
  child: ParentChild | null;
  organizationName?: string;
  organizationLogoUrl?: string;
  contactPhone?: string;
  contactEmail?: string;
  contactWebsite?: string;
}

const SETTLED_CHIP_SX = {
  height: 22,
  color: "common.white",
  "& .MuiChip-label": {
    color: "common.white",
    px: 1,
    fontSize: "0.7rem",
  },
} as const;

export const FeeStatementViewModal: React.FC<FeeStatementViewModalProps> = ({
  open,
  onClose,
  account,
  child,
  organizationName,
  organizationLogoUrl,
  contactPhone,
  contactEmail,
  contactWebsite,
}) => {
  if (!account || !child) {
    return (
      <ActionModal open={open} onClose={onClose} title="Fee statement">
        <Typography variant="body2" color="text.secondary">
          Fee statement unavailable.
        </Typography>
      </ActionModal>
    );
  }

  const childName = getChildFullName(child);
  const summary = summarizeSchoolFees(account);
  const ledger = buildFeeLedger(account);

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      title="Fee statement"
      maxWidth="md"
      footer={
        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "flex-end",
            alignItems: "center",
            width: "100%",
            flexWrap: "wrap",
          }}
        >
          <NoticeboardButton
            variant="secondary"
            size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
            startIcon={<DownloadOutlinedIcon />}
            onClick={() => {
              void downloadFeeStatementPdf(child, account, {
                organizationName,
                organizationLogoUrl,
                contact: {
                  phone: contactPhone,
                  email: contactEmail,
                  website: contactWebsite,
                },
              });
            }}
          >
            Download PDF
          </NoticeboardButton>
          <NoticeboardButton
            variant="primary"
            size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
            onClick={onClose}
          >
            Close
          </NoticeboardButton>
        </Box>
      }
    >
      <Box className="fee-statement-print" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {organizationName || organizationLogoUrl ? (
          <Stack direction="row" spacing={1} alignItems="center">
            {organizationLogoUrl && organizationLogoUrl !== "/placeholder.jpg" ? (
              <Box
                component="img"
                src={organizationLogoUrl}
                alt=""
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  objectFit: "contain",
                  bgcolor: "action.hover",
                }}
              />
            ) : null}
            {organizationName ? (
              <Typography variant="subtitle2" color="text.secondary">
                {organizationName}
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
          gap={1.5}
        >
          <Stack direction="row" spacing={1.25} alignItems="center" minWidth={0}>
            <Avatar
              sx={{
                width: 44,
                height: 44,
                fontSize: "0.85rem",
                fontWeight: 600,
                bgcolor: getAvatarColor(childName, lightTheme),
                color: "common.white",
              }}
            >
              {getChildInitials(child)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {childName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {account.academicYear} · {paymentPlanLabel(account.paymentPlanMonths)}
              </Typography>
            </Box>
          </Stack>
          <Chip
            size="small"
            label={summary.isSettled ? "Paid up" : "Outstanding"}
            color={summary.isSettled ? "success" : "warning"}
            sx={SETTLED_CHIP_SX}
          />
        </Stack>

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Table size="small">
            <TableBody>
              {[
                ["Child name", childName],
                ["Grade", child.grade || "—"],
                ["Full day / Half day", dayTypeLabel(account.dayType)],
                ["Payment plan", paymentPlanLabel(account.paymentPlanMonths)],
                [
                  `Total fee for ${account.academicYear}`,
                  formatLedgerCurrency(account.totalAnnualFee, account.currency),
                ],
              ].map(([label, value]) => (
                <TableRow key={label}>
                  <TableCell sx={{ fontWeight: 600, width: "40%" }}>{label}</TableCell>
                  <TableCell>{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Month</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Amount paid
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Outstanding
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Receipt No.</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ledger.map((row) => (
                  <TableRow key={row.paymentNumber}>
                    <TableCell>{row.monthLabel}</TableCell>
                    <TableCell>
                      {row.paidDate ? formatLedgerDate(row.paidDate) : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {row.amountPaid != null
                        ? formatLedgerCurrency(row.amountPaid, account.currency)
                        : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {row.amountOutstanding != null
                        ? formatLedgerCurrency(row.amountOutstanding, account.currency)
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {row.receiptNo ? `Receipt ${row.receiptNo}` : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              px: 2,
              py: 1.25,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              Amount due:{" "}
              <Box component="span" sx={{ color: "primary.main" }}>
                {summary.isSettled
                  ? "Paid up"
                  : formatLedgerCurrency(summary.outstanding, account.currency)}
              </Box>
            </Typography>
          </Box>
        </Box>
      </Box>
    </ActionModal>
  );
};

export default FeeStatementViewModal;
